import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  Shield, CheckCircle, XCircle, AlertTriangle, AlertOctagon,
  Download, Mail, Clock, FileText, ArrowRight, Loader2,
  ExternalLink, QrCode, RefreshCw, Home, Info, Activity,
  Zap, Flag, Star, Eye, TrendingUp, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Risk level configurations
const getRiskConfig = (riskLevel) => {
  const level = (riskLevel || '').toLowerCase();
  
  if (level.includes('critical') || level.includes('high')) {
    return { 
      level: riskLevel,
      badge: 'HIGH RISK',
      color: 'red', 
      icon: XCircle,
      bgClass: 'bg-red-500/10 border-red-500/30',
      textClass: 'text-red-400',
      badgeClass: 'bg-red-500',
      barClass: 'bg-red-500'
    };
  }
  if (level.includes('moderate') || level.includes('medium')) {
    return { 
      level: riskLevel,
      badge: 'CAUTION',
      color: 'orange', 
      icon: AlertOctagon,
      bgClass: 'bg-orange-500/10 border-orange-500/30',
      textClass: 'text-orange-400',
      badgeClass: 'bg-orange-500',
      barClass: 'bg-orange-500'
    };
  }
  if (level.includes('review') || level.includes('needs')) {
    return { 
      level: riskLevel,
      badge: 'REVIEW NEEDED',
      color: 'yellow', 
      icon: AlertTriangle,
      bgClass: 'bg-yellow-500/10 border-yellow-500/30',
      textClass: 'text-yellow-400',
      badgeClass: 'bg-yellow-500',
      barClass: 'bg-yellow-500'
    };
  }
  if (level.includes('trusted') || level.includes('verified')) {
    return { 
      level: riskLevel,
      badge: 'TRUSTED',
      color: 'green', 
      icon: CheckCircle,
      bgClass: 'bg-green-500/10 border-green-500/30',
      textClass: 'text-green-400',
      badgeClass: 'bg-green-500',
      barClass: 'bg-green-500'
    };
  }
  return { 
    level: riskLevel || 'Low Risk',
    badge: 'LOW RISK',
    color: 'green', 
    icon: CheckCircle,
    bgClass: 'bg-green-500/10 border-green-500/30',
    textClass: 'text-green-400',
    badgeClass: 'bg-green-500',
    barClass: 'bg-green-500'
  };
};

const InstantResultsPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  const [paymentVerified, setPaymentVerified] = useState(false);
  const [plan, setPlan] = useState('scam_check_basic');

  useEffect(() => {
    const verifyAndLoadResults = async () => {
      // PRIMARY: Get session_id from URL - this is the reliable identifier
      const sessionId = searchParams.get('session_id');
      const planParam = searchParams.get('plan') || 'scam_check_basic';
      setPlan(planParam);
      
      console.log('[RESULTS] session_id from URL:', sessionId);
      
      // SECONDARY: Try to get scan_token (may be null after Stripe redirect)
      let scanToken = searchParams.get('scan_token');
      console.log('[RESULTS] scan_token from URL:', scanToken);
      
      // Fallback to localStorage if URL token is missing or null
      if (!scanToken || scanToken === 'null' || scanToken === 'undefined') {
        scanToken = sessionStorage.getItem('scan_token');
        console.log('[RESULTS] scan_token from storage:', scanToken);
      }
      
      // Normalize null values
      if (scanToken === 'null' || scanToken === 'undefined') {
        scanToken = null;
      }
      
      console.log('[RESULTS] final scan_token:', scanToken);

      // CRITICAL: session_id is required, scan_token is optional
      if (!sessionId) {
        // Check for stored entity in sessionStorage (fallback flow)
        const storedEntity = sessionStorage.getItem('scam_check_entity');
        if (storedEntity) {
          try {
            const entity = JSON.parse(storedEntity);
            // Redirect to /check to complete payment
            navigate(`/check?entity=${encodeURIComponent(entity.query)}&type=${entity.type}`);
            return;
          } catch (e) {
            console.log('No stored entity');
          }
        }
        
        setError('No payment session found. Please complete payment first.');
        setLoading(false);
        return;
      }
      
      // NOTE: We proceed even if scan_token is null - backend will look up by session_id

      try {
        setLoading(true);
        
        // Build API URL - session_id is required, scan_token is optional
        let apiUrl = `/api/scam-check/process-payment?session_id=${encodeURIComponent(sessionId)}`;
        
        // Add scan_token only if it's valid (not null)
        if (scanToken) {
          apiUrl += `&scan_token=${encodeURIComponent(scanToken)}`;
        }
        
        console.log('[RESULTS] Calling API:', apiUrl);
        
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          console.error('[RESULTS] Backend error response:', errData);
          
          // Handle structured error from backend (detail can be string or object)
          let errorMessage = 'Failed to verify payment';
          if (errData.detail) {
            if (typeof errData.detail === 'string') {
              errorMessage = errData.detail;
            } else if (typeof errData.detail === 'object') {
              // Structured error object from backend
              errorMessage = errData.detail.message || errData.detail.error || JSON.stringify(errData.detail);
              console.error('[RESULTS] Structured error:', errData.detail);
            }
          }
          throw new Error(errorMessage);
        }

        const data = await response.json();
        console.log('[RESULTS] *** FULL API RESPONSE ***:', JSON.stringify(data, null, 2));
        
        // If API returns success: true → ALWAYS show results page
        if (data.success === true) {
          console.log('[RESULTS] SUCCESS - Rendering results page');
          setPaymentVerified(true);
          
          // Transform backend response to expected results format
          const resultsData = {
            entity: data.entity,
            entity_type: data.entity_type,
            risk_score: data.risk_score,
            risk_level: data.verdict_label || data.verdict,
            verdict: data.verdict_detail || data.verdict,
            verdict_color: data.verdict_color,
            confidence_score: data.confidence_score || 95,
            indicators: data.indicators || data.risk_factors?.map(rf => ({
              type: rf.severity === 'high' ? 'negative' : rf.severity === 'medium' ? 'warning' : 'info',
              text: rf.description || rf.factor,
              category: rf.category
            })) || [],
            risk_factors: data.risk_factors || [],
            positive_signals: data.positive_signals || [],
            recommendation: data.recommendation,
            case_reference: data.report_id,
            report_id: data.report_id,
            timestamp: data.analysis_timestamp,
            pdf_download_url: data.pdf_download_url,
            verification_url: data.verification_url,
            sources_checked: data.sources_checked,
            external_checks_performed: true
          };
          
          console.log('[RESULTS] Transformed resultsData:', resultsData);
          
          setResults(resultsData);
          setPlan(data.analysis_type === 'Advanced' ? 'scam_check_pro' : 'scam_check_basic');
          toast.success('Payment verified! Your fraud intelligence report is ready.');
          
          // Clear all storage after successful load
          sessionStorage.removeItem('scam_check_entity');
          sessionStorage.removeItem('scan_token');
          sessionStorage.removeItem('scan_entity');
          sessionStorage.removeItem('scan_entity_type');
          sessionStorage.removeItem('scan_plan');
          
          // Clean URL - keep on /instant-results (production route)
          window.history.replaceState({}, '', `/instant-results?verified=true&report=${data.report_id}`);
        } else {
          console.error('[RESULTS] API returned success: false or missing');
          throw new Error(data.message || data.error || 'Invalid response from server');
        }

      } catch (err) {
        console.error('[RESULTS] Verification error:', err);
        
        // Try to parse error detail if it's a structured error
        let errorMessage = err.message || 'Failed to load results. Please contact support.';
        
        // Check if it's a SESSION_NOT_FOUND error with debug info
        if (err.message && err.message.includes('SESSION_NOT_FOUND')) {
          errorMessage = 'Unable to retrieve scan data. This may be due to an expired session. Please try your purchase again.';
        }
        
        setError(errorMessage);
        toast.error('Failed to load results');
      } finally {
        setLoading(false);
      }
    };

    verifyAndLoadResults();
  }, [searchParams, navigate]);

  const handleDownloadPdf = () => {
    if (results?.pdf_download_url) {
      window.open(results.pdf_download_url, '_blank');
      toast.success('PDF download started');
    } else {
      toast.error('PDF not available');
    }
  };

  const handleVerifyReport = () => {
    if (results?.verification_url) {
      window.open(results.verification_url, '_blank');
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a1628] pt-24 pb-16 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="w-20 h-20 border-4 border-[#1e40af]/30 border-t-cyan-400 rounded-full animate-spin mx-auto"></div>
            <Shield className="w-8 h-8 text-cyan-400 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
          </div>
          <h2 className="text-2xl font-bold text-white mt-6 mb-2">Verifying Payment</h2>
          <p className="text-gray-400">Running fraud intelligence analysis...</p>
          <p className="text-gray-500 text-sm mt-4">This may take a few seconds</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-[#0a1628] pt-24 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <Card className="bg-red-500/10 border-red-500/30">
              <CardContent className="p-8 text-center">
                <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-white mb-4">Unable to Load Results</h2>
                <p className="text-gray-400 mb-6">{error}</p>
                <div className="flex gap-4 justify-center flex-wrap">
                  <Button 
                    onClick={() => navigate('/check')}
                    className="bg-cyan-600 hover:bg-cyan-700 text-white"
                  >
                    <Home className="w-4 h-4 mr-2" />
                    Start New Check
                  </Button>
                  <Button 
                    onClick={() => window.location.reload()}
                    variant="outline"
                    className="border-gray-600 text-gray-300 hover:bg-gray-800"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Try Again
                  </Button>
                </div>
                <p className="text-gray-500 text-sm mt-6">
                  Need help? Contact us at{' '}
                  <a href="mailto:info@actionfraudone.com" className="text-cyan-400 hover:underline">
                    info@actionfraudone.com
                  </a>
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Results state
  const riskConfig = results ? getRiskConfig(results.risk_level) : null;
  const RiskIcon = riskConfig?.icon || AlertTriangle;
  const isAdvanced = plan === 'scam_check_pro';

  return (
    <div className="min-h-screen bg-[#0a1628] pt-24 pb-16" data-testid="instant-results-page">
      <Helmet>
        <title>Fraud Intelligence Report | ActionFraudOne</title>
        <meta name="description" content="Your fraud intelligence report is ready. View risk assessment, indicators, and recommendations." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          
          {/* Payment Verified Badge */}
          {paymentVerified && (
            <div className="flex items-center justify-center gap-2 mb-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/30 rounded-full">
                <CheckCircle className="w-4 h-4 text-green-400" />
                <span className="text-green-400 text-sm font-medium">Payment Verified</span>
              </div>
              {isAdvanced && (
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/10 border border-purple-500/30 rounded-full">
                  <Star className="w-4 h-4 text-purple-400" />
                  <span className="text-purple-400 text-sm font-medium">Advanced Analysis</span>
                </div>
              )}
            </div>
          )}

          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
              Fraud Intelligence Report
            </h1>
            {results?.case_reference && (
              <p className="text-gray-400">
                Case Reference: <span className="text-[#C3A86B] font-mono">{results.case_reference}</span>
              </p>
            )}
          </div>

          {results && (
            <>
              {/* Main Results Card */}
              <Card 
                className={`${riskConfig.bgClass} border-2 mb-8`}
                data-testid="results-card"
              >
                <CardContent className="p-6 md:p-8">
                  {/* Risk Header */}
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                    <div className="flex-1">
                      <span className={`inline-block px-4 py-1.5 ${riskConfig.badgeClass} text-white text-sm font-bold rounded-full mb-3`} data-testid="risk-badge">
                        {riskConfig.badge}
                      </span>
                      <h2 className="text-xl font-semibold text-white mb-1">Entity Checked</h2>
                      <p className="text-gray-300 text-lg break-all">{results.entity}</p>
                      <p className="text-gray-500 text-sm capitalize">{results.entity_type?.replace('_', ' ')}</p>
                    </div>
                    <div className={`w-24 h-24 ${riskConfig.bgClass} border ${riskConfig.textClass.replace('text-', 'border-')} rounded-xl flex items-center justify-center flex-shrink-0`}>
                      <RiskIcon className={`w-12 h-12 ${riskConfig.textClass}`} />
                    </div>
                  </div>

                  {/* Risk Score Bar */}
                  <div className="mb-6" data-testid="risk-score">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-gray-400 font-medium">Fraud Risk Score</span>
                      <span className={`font-bold text-2xl ${riskConfig.textClass}`}>
                        {results.risk_score}/100
                      </span>
                    </div>
                    <div className="h-4 bg-[#0a1628] rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${riskConfig.barClass} rounded-full transition-all duration-1000 ease-out`}
                        style={{ width: `${results.risk_score}%` }}
                      />
                    </div>
                    {results.confidence_score && (
                      <p className="text-gray-500 text-xs mt-2">
                        Analysis confidence: {results.confidence_score}%
                      </p>
                    )}
                  </div>

                  {/* Verdict */}
                  <div className="bg-[#0a1628]/70 rounded-lg p-4 mb-6" data-testid="verdict">
                    <div className="flex items-center gap-2 mb-2">
                      <Activity className={`w-5 h-5 ${riskConfig.textClass}`} />
                      <span className="text-white font-semibold">Verdict</span>
                    </div>
                    <p className="text-gray-300">{results.verdict}</p>
                  </div>

                  {/* Risk Indicators */}
                  {results.indicators && results.indicators.length > 0 && (
                    <div className="mb-6" data-testid="indicators">
                      <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                        <Eye className="w-5 h-5 text-cyan-400" />
                        Risk Indicators Detected
                      </h3>
                      <div className="space-y-2">
                        {results.indicators.map((indicator, index) => {
                          const styles = {
                            positive: { icon: CheckCircle, class: 'text-green-400 bg-green-500/10 border-green-500/20' },
                            negative: { icon: XCircle, class: 'text-red-400 bg-red-500/10 border-red-500/20' },
                            warning: { icon: AlertTriangle, class: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20' },
                            info: { icon: Info, class: 'text-blue-400 bg-blue-500/10 border-blue-500/20' }
                          };
                          const style = styles[indicator.type] || styles.info;
                          const Icon = style.icon;
                          
                          return (
                            <div key={index} className={`flex items-start gap-3 p-3 ${style.class} border rounded-lg`}>
                              <Icon className="w-4 h-4 flex-shrink-0 mt-0.5" />
                              <div>
                                <span className="text-sm text-gray-300">{indicator.text}</span>
                                {indicator.category && (
                                  <span className="ml-2 text-xs text-gray-500">({indicator.category})</span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Recommendation */}
                  <div className={`p-4 ${riskConfig.bgClass} border rounded-lg`} data-testid="recommendation">
                    <h3 className="text-white font-semibold mb-2">Recommended Action</h3>
                    <p className="text-gray-300 text-sm">{results.recommendation}</p>
                  </div>
                </CardContent>
              </Card>

              {/* Actions Card - PDF Download */}
              <Card className="bg-[#0d1d32] border-[#1e40af]/30 mb-8">
                <CardContent className="p-6">
                  <div className="flex flex-col sm:flex-row gap-4">
                    {/* Download PDF */}
                    {results.pdf_download_url && (
                      <Button 
                        onClick={handleDownloadPdf}
                        className="flex-1 bg-[#C3A86B] hover:bg-[#b39759] text-black font-semibold py-6"
                        data-testid="download-pdf-btn"
                      >
                        <Download className="w-5 h-5 mr-2" />
                        Download PDF Report
                      </Button>
                    )}
                    
                    {/* Verification Link */}
                    {results.verification_url && (
                      <Button 
                        onClick={handleVerifyReport}
                        variant="outline"
                        className="flex-1 border-[#1e40af]/50 text-gray-300 hover:bg-[#1e40af]/10 py-6"
                      >
                        <QrCode className="w-5 h-5 mr-2" />
                        Verify Report
                      </Button>
                    )}
                  </div>
                  
                  {!results.pdf_download_url && (
                    <p className="text-gray-500 text-sm text-center mt-4">
                      PDF report is being generated...
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Case Details */}
              <Card className="bg-[#0d1d32] border-[#1e40af]/30 mb-8">
                <CardContent className="p-6">
                  <h3 className="text-white font-semibold mb-4">Report Details</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Case Reference</p>
                      <p className="text-[#C3A86B] font-mono">{results.case_reference}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Generated</p>
                      <p className="text-gray-300">
                        {results.timestamp ? new Date(results.timestamp).toLocaleString() : 'Just now'}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Entity Type</p>
                      <p className="text-gray-300 capitalize">{results.entity_type?.replace('_', ' ')}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Analysis Type</p>
                      <p className="text-gray-300">{isAdvanced ? 'Advanced Risk Analysis' : 'Instant Scam Check'}</p>
                    </div>
                  </div>
                  
                  {/* Sources List */}
                  {results.sources_checked && results.sources_checked.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-[#1e40af]/20">
                      <p className="text-gray-500 text-sm mb-2">Intelligence Sources Reviewed:</p>
                      <div className="flex flex-wrap gap-2">
                        {results.sources_checked.map((source, i) => (
                          <span key={i} className="px-2 py-1 bg-[#1e40af]/10 border border-[#1e40af]/20 rounded text-xs text-gray-400">
                            {source}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {results.external_checks_performed && (
                    <div className="mt-4 flex items-center gap-2 text-green-400 text-sm">
                      <CheckCircle className="w-4 h-4" />
                      External threat intelligence checks performed
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Upgrade CTA for basic plan with high risk */}
              {!isAdvanced && results.risk_score >= 50 && (
                <Card className="bg-gradient-to-r from-purple-500/10 to-cyan-500/10 border-purple-500/30 mb-8">
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Zap className="w-5 h-5 text-yellow-400" />
                          <span className="text-yellow-400 font-medium text-sm">NEED DEEPER ANALYSIS?</span>
                        </div>
                        <h3 className="text-xl font-bold text-white mb-2">Advanced Risk Analysis</h3>
                        <p className="text-gray-400">
                          Get pattern detection, detailed recommendations, and enhanced threat intelligence for just £9.99.
                        </p>
                      </div>
                      <Button 
                        onClick={() => navigate('/pricing')}
                        className="bg-purple-600 hover:bg-purple-700 text-white font-semibold px-6 py-6 flex-shrink-0"
                      >
                        <Star className="w-5 h-5 mr-2" />
                        Upgrade Now
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Investigation CTA for very high risk */}
              {results.risk_score >= 70 && (
                <Card className="bg-[#0d1d32] border-orange-500/30 mb-8">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <FileText className="w-10 h-10 text-orange-400 flex-shrink-0" />
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-white mb-2">Need Legal-Ready Evidence?</h3>
                        <p className="text-gray-400 text-sm mb-4">
                          Our expert investigators can conduct a comprehensive fraud assessment with evidence documentation 
                          suitable for police reports, bank claims, and legal proceedings.
                        </p>
                        <Button 
                          onClick={() => navigate('/investigations')}
                          variant="outline"
                          className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                        >
                          View Investigation Services
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {/* Footer Links */}
          <div className="text-center mt-8 space-y-4">
            <div>
              <Link to="/check" className="text-cyan-400 hover:text-cyan-300 text-sm">
                Run Another Check
              </Link>
              <span className="text-gray-600 mx-3">|</span>
              <Link to="/" className="text-gray-400 hover:text-gray-300 text-sm">
                Back to Home
              </Link>
            </div>
            
            {results && (
              <Link 
                to={`/report-a-scam?entity=${encodeURIComponent(results.entity || '')}&type=${results.entity_type || ''}`}
                className="inline-flex items-center gap-2 text-gray-500 hover:text-red-400 transition-colors text-sm"
              >
                <Flag className="w-4 h-4" />
                Report this entity as a scam
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstantResultsPage;
